import { useEffect, useState } from "react";
import { Loader2, GraduationCap, Clock, Zap, PlayCircle, CheckCircle2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const TRIGGER = { Incident: "#F04438", Compliance: "#F79009", Certification: "#7C3AED", Onboarding: "#155EEF" };
const CAT = { Medication: "#155EEF", Documentation: "#7C3AED", Safety: "#F79009", Compliance: "#12B76A", Clinical: "#DB2777" };

export default function Training() {
  const [courses, setCourses] = useState([]);
  const [assigns, setAssigns] = useState(null);

  const load = () => api.get("/training/assignments").then((r) => setAssigns(r.data)).catch(() => setAssigns([]));
  useEffect(() => { load(); api.get("/training/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/training/assignments/${id}/status`, { status });
      toast.success(status === "completed" ? "Training completed — filed to compliance" : "Training started"); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  if (!assigns) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const counts = assigns.reduce((a, x) => { a[x.status] = (a[x.status] || 0) + 1; return a; }, {});

  return (
    <div>
      <PageHeader overline="Workforce · Adaptive Training & Talent Intelligence" title="Training"
        description="Event-driven microlearning. Incidents, compliance findings and expiring certifications automatically assign targeted 5–15 minute modules." />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={Zap} label="Assigned" value={counts.assigned || 0} accent="#F79009" index={0} />
        <StatCard icon={PlayCircle} label="In Progress" value={counts["in-progress"] || 0} accent="#155EEF" index={1} />
        <StatCard icon={CheckCircle2} label="Completed" value={counts.completed || 0} accent="#12B76A" index={2} />
        <StatCard icon={BookOpen} label="Course Library" value={courses.length} accent="#7C3AED" index={3} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Assignments */}
        <div className="lg:col-span-7">
          <h2 className="mb-3 text-lg font-bold tracking-tight">Assignments (event-driven)</h2>
          <div className="space-y-3">
            {assigns.map((a) => (
              <div key={a.id} data-testid={`assign-${a.id}`} className="rounded-xl border bg-card p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: TRIGGER[a.trigger] || "#155EEF", backgroundColor: (TRIGGER[a.trigger] || "#155EEF") + "1a" }}>
                      <Zap size={10} className="mr-1 inline" />{a.trigger}
                    </span>
                    <span className="text-sm font-semibold">{a.course_title}</span>
                  </div>
                  <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold capitalize",
                    a.status === "completed" ? "bg-[#12B76A1a] text-[#12B76A]" : a.status === "in-progress" ? "bg-[#155EEF1a] text-[#155EEF]" : "bg-[#F790091a] text-[#F79009]")}>
                    {a.status.replace("-", " ")}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{a.assignee} · {a.reason} · due {a.due_date}</div>
                {a.status !== "completed" && (
                  <div className="mt-3 flex gap-2">
                    {a.status === "assigned" && (
                      <button data-testid={`start-${a.id}`} onClick={() => setStatus(a.id, "in-progress")}
                        className="flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium hover:border-[#155EEF] hover:text-[#155EEF]"><PlayCircle size={13} /> Start</button>
                    )}
                    <button data-testid={`complete-assign-${a.id}`} onClick={() => setStatus(a.id, "completed")}
                      className="flex items-center gap-1 rounded-md bg-[#12B76A] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#0F9A5A]"><CheckCircle2 size={13} /> Complete</button>
                  </div>
                )}
              </div>
            ))}
            {assigns.length === 0 && <div className="rounded-xl border bg-card p-8 text-center text-muted-foreground">No active assignments.</div>}
          </div>
        </div>

        {/* Course library */}
        <div className="lg:col-span-5">
          <h2 className="mb-3 text-lg font-bold tracking-tight">Course Library</h2>
          <div className="space-y-3">
            {courses.map((c) => (
              <div key={c.id} data-testid={`course-${c.id}`} className="rounded-xl border bg-card p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: CAT[c.category] || "#155EEF", backgroundColor: (CAT[c.category] || "#155EEF") + "1a" }}>{c.category}</span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={12} /> {c.minutes} min</span>
                </div>
                <h3 className="mt-2 flex items-center gap-2 text-sm font-semibold"><GraduationCap size={15} className="text-[#7C3AED]" /> {c.title}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{c.description}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {c.steps.map((s, i) => <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{i + 1}. {s}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
